import { useMemo } from 'react';
import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRisks, type Risk } from 'queries/risks';

/**
 * Fallback catalog (colors + order) mirroring the wio.api seed, used when
 * `/risks` is unavailable so el resultado se sigue pintando con los colores.
 */
const FALLBACK_RISKS: Risk[] = [
  { id: 1, key: 'risk.defensive', order: 1, color: '#4E5BA6' },
  { id: 2, key: 'risk.conservative', order: 2, color: '#7EBC25' },
  { id: 3, key: 'risk.moderate', order: 3, color: '#EAAA08' },
  { id: 4, key: 'risk.dynamic', order: 4, color: '#EF6820' },
  { id: 5, key: 'risk.aggressive', order: 5, color: '#F63D68' },
];

interface ReviewStepProps {
  /** Ordinal del perfil (calculado por preview); undefined mientras carga. */
  profileOrder?: number;
  isLoading: boolean;
}

export const ReviewStep = ({ profileOrder, isLoading }: ReviewStepProps) => {
  const { t } = useTranslation();
  const { data: risksData } = useRisks();

  // wio.front pinta el medidor con los colores del catálogo de /risks; aquí
  // hacemos lo mismo y caemos al seed si la llamada aún no resolvió.
  const risks = useMemo(() => {
    const list = risksData?.length ? risksData : FALLBACK_RISKS;
    return [...list].sort((a, b) => a.order - b.order);
  }, [risksData]);

  return (
    <Box>
      <Typography variant="textXl" sx={{ fontWeight: 700, mb: 1 }}>
        {t('review.title')}
      </Typography>
      <Typography
        variant="textSm"
        sx={{ color: 'text.secondary', mb: 3, display: 'block' }}
      >
        {t('review.subtitle')}
      </Typography>

      {isLoading && (
        <Paper
          variant="outlined"
          sx={{ p: 5, borderRadius: 3, textAlign: 'center' }}
        >
          <CircularProgress size={28} />
          <Typography
            variant="textSm"
            sx={{ color: 'text.secondary', mt: 2, display: 'block' }}
          >
            {t('review.calculating')}
          </Typography>
        </Paper>
      )}

      {profileOrder != null && (
        <ProfileResult profileOrder={profileOrder} risks={risks} />
      )}
    </Box>
  );
};

interface ProfileResultProps {
  profileOrder: number;
  risks: Risk[];
}

const ProfileResult = ({ profileOrder, risks }: ProfileResultProps) => {
  const { t } = useTranslation();
  // El perfil se casa con el catálogo /risks por su `order` (puente cross-empresa).
  // `risk.key` es la clave i18n.
  const index = Math.max(
    0,
    risks.findIndex((r) => r.order === profileOrder),
  );
  const risk = risks[index];
  const color = risk?.color ?? '#334155';
  const label = risk ? t(risk.key) : '';

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack gap={3} alignItems="center" textAlign="center" sx={{ p: 4 }}>
        <Stack gap={0.5} alignItems="center">
          <Typography
            variant="textSm"
            sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              fontSize: 11,
            }}
          >
            {t('review.riskProfile')}
          </Typography>
          <Typography variant="displaySm" sx={{ fontWeight: 700, color }}>
            {label}
          </Typography>
          <Typography variant="textXs" sx={{ color: 'text.secondary' }}>
            {t('review.level', { current: index + 1, total: risks.length })}
          </Typography>
        </Stack>

        {/* Medidor de riesgo (colores del catálogo /risks, como wio.front) */}
        <Box sx={{ width: '100%', maxWidth: 360 }}>
          {/* Segmentos */}
          <Stack direction="row" gap={0.75}>
            {risks.map((r, i) => (
              <Box
                key={r.id}
                sx={{
                  flex: 1,
                  height: i === index ? 10 : 6,
                  alignSelf: 'center',
                  borderRadius: 999,
                  bgcolor: r.color,
                  opacity: i === index ? 1 : 0.45,
                  boxShadow: i === index ? `0 0 0 3px ${color}26` : 'none',
                  transition: 'all .2s',
                }}
              />
            ))}
          </Stack>
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
            <Typography variant="textXs" sx={{ color: 'text.secondary' }}>
              {t('review.lowerRisk')}
            </Typography>
            <Typography variant="textXs" sx={{ color: 'text.secondary' }}>
              {t('review.higherRisk')}
            </Typography>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
};
